import React, { useEffect, useRef, useState } from "react";

/**
 * HeroBackdrop — cinematic hero background.
 *
 * Cycles through a curated set of fitness photos with:
 *   - Cross-fade transition (1.2s opacity)
 *   - Ken Burns slow-pan + slow-zoom while a photo is on-screen
 *   - Pause when the tab is hidden (saves battery on mobile)
 *   - Reduced-motion safe (skips animation; falls back to a single still)
 *   - Per-image error fallback so a 404 never blanks the hero
 *
 * Intentionally decoupled from the rest of the page — drop it in as the
 * first child of the hero `<section>` and it just works.
 */
const PHOTOS = [
  // Existing — strong barbell-grip torso shot (Pexels)
  "https://images.pexels.com/photos/13327298/pexels-photo-13327298.jpeg?auto=compress&cs=tinysrgb&w=1920",
  // Determined athlete with dumbbell (Unsplash — Anastase Maragos)
  "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&w=1920&q=80",
  // Heavy compound lift mid-rep (Unsplash — Victor Freitas)
  "https://images.unsplash.com/photo-1517836357463-d25dfeac3438?auto=format&fit=crop&w=1920&q=80",
  // Low-light gym focus (Unsplash — Alora Griffiths)
  "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?auto=format&fit=crop&w=1920&q=80",
];

const INTERVAL_MS = 7000; // photo dwell time

export default function HeroBackdrop({ photos = PHOTOS, intervalMs = INTERVAL_MS }) {
  const [index, setIndex] = useState(0);
  // Per-slot Ken Burns variant key — flips each rotation so the next photo
  // animates from a fresh starting point rather than restarting the same
  // pan every time. Reads "0" | "1" | "2" cycling.
  const variantRef = useRef(0);

  // Drop any photo that fails to load so we never freeze on a 404.
  const [aliveSet, setAliveSet] = useState(() => new Set(photos.map((_, i) => i)));
  const livePhotos = photos.filter((_, i) => aliveSet.has(i));

  useEffect(() => {
    // Respect reduced motion — show only the first image, no cycling.
    if (typeof window === "undefined") return undefined;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || livePhotos.length <= 1) return undefined;

    const tick = () => {
      // Skip rotation while the page is hidden (mobile background tab).
      if (document.hidden) return;
      variantRef.current = (variantRef.current + 1) % 3;
      setIndex((i) => (i + 1) % livePhotos.length);
    };
    const id = window.setInterval(tick, intervalMs);
    return () => window.clearInterval(id);
  }, [livePhotos.length, intervalMs]);

  if (livePhotos.length === 0) {
    return <div className="absolute inset-0 bg-[#0a0a0a]" aria-hidden="true" />;
  }

  return (
    <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
      {photos.map((src, i) => {
        if (!aliveSet.has(i)) return null;
        const isActive = livePhotos.indexOf(src) === index;
        return (
          <img
            key={src}
            src={src}
            alt=""
            draggable="false"
            onError={() =>
              setAliveSet((prev) => {
                const next = new Set(prev);
                next.delete(i);
                return next;
              })
            }
            className={`hero-backdrop-img ken-burns-${variantRef.current} ${
              isActive ? "is-active" : ""
            }`}
            // The cycling key forces a fresh animation per cycle so the
            // Ken Burns doesn't snap mid-pan when the active photo changes.
            style={{ animationName: isActive ? undefined : "none" }}
          />
        );
      })}
    </div>
  );
}
